import {
  GetSaleRes_LinkedOrder as LinkedOrderPB,
  GetSaleRes_LinkedOrders as LinkedOrdersPB,
  GetSaleRes_LinkedShipment as LinkedShipmentPB,
  GetSaleRes_LinkedShipments as LinkedShipmentsPB,
} from "gen/api/sale/service_pb"
import { SaleProduct as SaleProductPB } from "gen/txn/models_pb"
import { FieldMapping, mapFields } from "shared-ts/adapters"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import {
  pbOrderStatusToBaseOrderStatus,
  pbShipmentStatusToOrderShipmentStatus,
} from "shared-ts/types/orders"
import { ensure } from "shared-ts/utils"

import {
  LinkedOrder,
  LinkedShipment,
  SaleProduct,
  SaleProductWithOrderInfo,
} from "../types/SaleForm.types"

const linkedOrderMapping: FieldMapping<LinkedOrderPB, LinkedOrder> = {
  id: (source) => ensure(source.orderId, "Missing order ID"),
  identifier: (source) =>
    ensure(source.orderIdentifier, "Missing order identifier"),
  status: (source) => pbOrderStatusToBaseOrderStatus(source.orderStatus),
  quantity: (source) => Decimal.fromPBOrZero(source.quantity),
  quantityReceived: (source) => Decimal.fromPBOrZero(source.quantityReceived),
}

const linkedShipmentMapping: FieldMapping<LinkedShipmentPB, LinkedShipment> = {
  id: (source) => ensure(source.shipmentId, "Missing shipment ID"),
  identifier: (source) =>
    ensure(source.shipmentIdentifier, "Missing shipment identifier"),
  status: (source) =>
    pbShipmentStatusToOrderShipmentStatus(source.shipmentStatus),
  quantity: (source) => Decimal.fromPBOrZero(source.quantity),
}

export function buildSaleProducts(
  saleProductsV2: SaleProductPB[] | undefined
): SaleProduct[] {
  if (!saleProductsV2) {
    return []
  }
  return saleProductsV2.map((item) => SaleProductPB.create(item))
}

export function toSaleProductsWithOrderInfo(
  saleProducts: SaleProduct[],
  linkedOrdersBySaleProductId: Record<string, LinkedOrder[]> = {},
  linkedShipmentsBySaleProductId: Record<string, LinkedShipment[]> = {}
): SaleProductWithOrderInfo[] {
  return saleProducts.map((sp) => {
    const id = UUID.idString(sp.id)
    return {
      ...sp,
      linkedOrders: linkedOrdersBySaleProductId[id] ?? [],
      linkedShipments: linkedShipmentsBySaleProductId[id] ?? [],
    }
  })
}

export function buildSaleProductsWithLinkedOrder(
  saleProductsV2: SaleProductPB[] | undefined,
  linkedOrdersListBySaleProductId: Record<string, LinkedOrdersPB> = {},
  linkedShipmentsBySaleProductId: Record<string, LinkedShipmentsPB> = {}
): SaleProductWithOrderInfo[] {
  const sps = buildSaleProducts(saleProductsV2)
  const results: SaleProductWithOrderInfo[] = []
  sps.forEach((sp) => {
    const linkedOrders: LinkedOrder[] = []
    const linkedShipments: LinkedShipment[] = []
    linkedOrdersListBySaleProductId[UUID.idString(sp.id)]?.orders?.forEach(
      (linkedOrderPB) => {
        const { data: linkedOrder, errors: linkedOrderErrors } = mapFields(
          linkedOrderPB,
          linkedOrderMapping
        )
        // Received orders are accounted for through shipments
        if (!linkedOrderErrors && linkedOrder.status !== "ReceivedOrder") {
          linkedOrders.push(linkedOrder)
        }
      }
    )
    linkedShipmentsBySaleProductId[UUID.idString(sp.id)]?.shipments?.forEach(
      (linkedShipmentPB) => {
        const { data: linkedShipment, errors: linkedShipmentErrors } =
          mapFields(linkedShipmentPB, linkedShipmentMapping)
        if (!linkedShipmentErrors) {
          linkedShipments.push(linkedShipment)
        }
      }
    )
    results.push({ ...sp, linkedOrders, linkedShipments })
  })
  return results
}
